import { FC, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEndpoints } from '../../../hooks/useEndpoints';
import TeamBodyMapDay from '../TeamBodyMapDay/TeamBodyMapDay';
import ZoneAthletesModal from './ZoneAthletesModal';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import KpiCard from '../../../shared/KpiCard/KpiCard';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { ZONE_LABELS, getIntensityLabel } from '../../../shared/bodyMap/bodyMapScale';
import AnatomyFigure from '../../../shared/bodyMap/AnatomyFigure';
import { PulseIcon } from '@phosphor-icons/react';
import styles from './TeamBodyMapHeatmap.module.scss';

interface HeatmapZone {
  zone: string;
  athleteCount: number;
  totalComplaints: number;
  maxIntensity: number;
}

interface TeamHeatmapResponse {
  teamId: string;
  period: string;
  zones: HeatmapZone[];
  athleteCount: number;
}

const PERIOD_LABELS: Record<string, string> = {
  '7d': '7 giorni',
  '14d': '14 giorni',
  '30d': '30 giorni',
};

// ─── Scala intensità → fascia (colore/classi in TeamBodyMapHeatmap.module.scss) ─
type IntensityBand = 'mild' | 'moderate' | 'intense';

/** Fascia di intensità (per scegliere la classe CSS), coerente con `getIntensityLabel`. */
function intensityBand(maxIntensity: number): IntensityBand {
  const label = getIntensityLabel(maxIntensity);
  if (label === 'Intensa') return 'intense';
  if (label === 'Moderata') return 'moderate';
  return 'mild';
}

const ROW_ZONE_DOT_CLASS: Record<IntensityBand, string> = {
  mild: styles.rowZoneDotMild,
  moderate: styles.rowZoneDotModerate,
  intense: styles.rowZoneDotIntense,
};

const RATIO_FILL_CLASS: Record<IntensityBand, string> = {
  mild: styles.ratioFillMild,
  moderate: styles.ratioFillModerate,
  intense: styles.ratioFillIntense,
};

/**
 * Diffusione (quota di atleti coinvolti nella zona) → opacità del fill sulla
 * figura: pochi atleti = più pallido, tutti = pieno. Stessa semantica di prima,
 * ora applicata ai muscoli della figura anatomica via AnatomyFigure.zoneOpacity.
 */
function heatOpacity(ratio: number): number {
  return 0.32 + Math.max(0, Math.min(1, ratio)) * 0.63;
}

// ─── Main component ───────────────────────────────────────────────────
interface Props {
  teamId: string;
}

const TeamBodyMapHeatmap: FC<Props> = ({ teamId }) => {
  const [period, setPeriod] = useState<'7d' | '14d' | '30d'>('7d');
  const [view, setView] = useState<'front' | 'back' | 'both'>('both');
  const [hoveredZone, setHoveredZone] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const endpoints = useEndpoints();

  const { data, isLoading, isError, refetch } = useQuery<TeamHeatmapResponse>({
    queryKey: ['bodymap', 'team', teamId, period],
    queryFn: async () => {
      const { data } = await endpoints.getBodyMapTeam(teamId, { period });
      return data;
    },
    enabled: !!teamId,
    staleTime: 1000 * 60 * 5,
  });

  const totalAthletes = data?.athleteCount ?? 0;

  // Zona → intensità max (colore) e zona → opacità (diffusione) per la figura.
  const zoneIntensity = useMemo(
    () => new Map<string, number>((data?.zones ?? []).map((z) => [z.zone, z.maxIntensity])),
    [data]
  );
  const zoneOpacity = useMemo(
    () =>
      new Map<string, number>(
        (data?.zones ?? []).map((z) => [
          z.zone,
          heatOpacity(totalAthletes > 0 ? z.athleteCount / totalAthletes : 0),
        ])
      ),
    [data, totalAthletes]
  );

  const sortedZones = useMemo(
    () => [...(data?.zones ?? [])].sort(
      (a, b) => b.athleteCount - a.athleteCount || b.maxIntensity - a.maxIntensity
    ),
    [data]
  );

  const activeZones = data?.zones.length ?? 0;
  const totalComplaints = (data?.zones ?? []).reduce((s, z) => s + z.totalComplaints, 0);
  const athletesInvolved = (data?.zones ?? []).reduce(
    (s, z) => Math.max(s, z.athleteCount),
    0
  );

  // Figure più piccole in "Entrambe" (due affiancate nella colonna 300px).
  const figureHeight = view === 'both' ? 300 : 360;

  return (
    <div className={styles.container}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <div>
          <div className={styles.title}>Heatmap fastidi team</div>
          <div className={styles.subtitle}>
            Ultimi {PERIOD_LABELS[period]} · {activeZones} zon{activeZones === 1 ? 'a' : 'e'} con segnalazioni
          </div>
        </div>
        <div className={styles.periodToggle}>
          {(['7d', '14d', '30d'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={[
                styles.periodBtn,
                period === p ? styles.periodBtnActive : '',
              ].filter(Boolean).join(' ')}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <LeopartLoader />
        </div>
      ) : isError ? (
        <div className={styles.errorState}>
          <ChartErrorState onRetry={() => refetch()} />
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className={styles.kpiRow}>
            <KpiCard label="Atleti coinvolti" value={`${athletesInvolved}/${totalAthletes}`} />
            <KpiCard label="Zone attive" value={String(activeZones)} />
            <KpiCard label="Segnalazioni" value={String(totalComplaints)} />
          </div>

          {/* Main grid */}
          <div className={styles.main}>
            {/* Body column */}
            <div className={styles.bodyCol}>
              <div className={styles.viewToggle}>
                {([
                  { v: 'front', l: 'Frontale' },
                  { v: 'back', l: 'Posteriore' },
                  { v: 'both', l: 'Entrambe' },
                ] as const).map(({ v, l }) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={[
                      styles.viewBtn,
                      view === v ? styles.viewBtnActive : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {l}
                  </button>
                ))}
              </div>

              <div className={styles.svgRow}>
                {(view === 'front' || view === 'both') && (
                  <div className={styles.svgColumn}>
                    <AnatomyFigure
                      view="front"
                      zoneIntensity={zoneIntensity}
                      zoneOpacity={zoneOpacity}
                      hoveredZone={hoveredZone}
                      onHoverZone={setHoveredZone}
                      onSelectZone={setSelectedZone}
                      heightPx={figureHeight}
                    />
                    <span className={styles.viewLabel}>Frontale</span>
                  </div>
                )}
                {(view === 'back' || view === 'both') && (
                  <div className={styles.svgColumn}>
                    <AnatomyFigure
                      view="back"
                      zoneIntensity={zoneIntensity}
                      zoneOpacity={zoneOpacity}
                      hoveredZone={hoveredZone}
                      onHoverZone={setHoveredZone}
                      onSelectZone={setSelectedZone}
                      heightPx={figureHeight}
                    />
                    <span className={styles.viewLabel}>Posteriore</span>
                  </div>
                )}
              </div>

              {/* Legend */}
              <div className={styles.legend}>
                <div className={styles.legendRow}>
                  <span className={styles.legendKey}>Intensità</span>
                  <span className={styles.legendChip}>
                    <span className={[styles.legendDot, styles.legendDotMild].join(' ')} />
                    Lieve
                  </span>
                  <span className={styles.legendChip}>
                    <span className={[styles.legendDot, styles.legendDotModerate].join(' ')} />
                    Moderata
                  </span>
                  <span className={styles.legendChip}>
                    <span className={[styles.legendDot, styles.legendDotIntense].join(' ')} />
                    Intensa
                  </span>
                </div>
                <div className={styles.legendRow}>
                  <span className={styles.legendKey}>Diffusione</span>
                  <div className={styles.gradientBar} />
                  <span className={styles.legendHint}>pochi → tutti</span>
                </div>
              </div>
            </div>

            {/* List column */}
            <div className={styles.listCol}>
              <div className={styles.listHeader}>
                <span className={styles.listTitle}>Zone per diffusione</span>
                <span className={styles.listMeta}>
                  {sortedZones.length} segnala{sortedZones.length === 1 ? 'ta' : 'te'}
                </span>
              </div>

              {sortedZones.length === 0 ? (
                <div className={styles.listEmpty}>
                  <PulseIcon className={styles.listEmptyIcon} size={30} weight="regular" />
                  <div className={styles.listEmptyTitle}>Nessun fastidio nel periodo</div>
                  <div className={styles.listEmptyHint}>
                    Nessuna segnalazione negli ultimi {PERIOD_LABELS[period]}. Prova
                    ad allargare il periodo qui sopra.
                  </div>
                </div>
              ) : (
                <div className={styles.list}>
                  {sortedZones.map((z, i) => {
                    const ratio = totalAthletes > 0 ? z.athleteCount / totalAthletes : 0;
                    const isActive = hoveredZone === z.zone;
                    const band = intensityBand(z.maxIntensity);
                    return (
                      <div
                        key={z.zone}
                        onMouseEnter={() => setHoveredZone(z.zone)}
                        onMouseLeave={() => setHoveredZone(null)}
                        onClick={() => setSelectedZone(z.zone)}
                        className={[styles.row, isActive ? styles.rowActive : '']
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <span className={styles.rowIndex}>
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <div className={styles.rowZone}>
                          <span className={[styles.rowZoneDot, ROW_ZONE_DOT_CLASS[band]].join(' ')} />
                          <span className={styles.rowZoneName}>
                            {ZONE_LABELS[z.zone] ?? z.zone}
                          </span>
                        </div>
                        <span className={styles.rowCount}>
                          <strong>{z.athleteCount}</strong>
                          <span className={styles.rowCountTotal}>/{totalAthletes}</span>
                        </span>
                        <div className={styles.ratioBar}>
                          <div
                            className={[styles.ratioFill, RATIO_FILL_CLASS[band]].join(' ')}
                            style={{ width: `${Math.round(ratio * 100)}%` }}
                          />
                        </div>
                        <span className={styles.rowMax}>{z.maxIntensity}/5</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Dettaglio giornaliero integrato — atleti del team per data */}
          <div className={styles.dailySection}>
            <TeamBodyMapDay teamId={teamId} />
          </div>
        </>
      )}

      {selectedZone && (
        <ZoneAthletesModal
          teamId={teamId}
          zone={selectedZone}
          zoneLabel={ZONE_LABELS[selectedZone] ?? selectedZone}
          period={period}
          periodLabel={PERIOD_LABELS[period]}
          onClose={() => setSelectedZone(null)}
        />
      )}
    </div>
  );
};

export default TeamBodyMapHeatmap;
